import React, { useState, useEffect, useId } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  useEffect(() => { if (user) navigate(user.role === "connector" ? "/connector" : "/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.role === "connector" ? "/connector" : "/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Founder access" subtitle="Sign in to manage investor access, magic links and engagement analytics.">
      <form onSubmit={submit} className="space-y-4" noValidate aria-describedby={error ? errorId : undefined}>
        <div className="space-y-1.5">
          <Label htmlFor={emailId} className="font-mono-data text-xs uppercase tracking-wider text-ftext2">
            Email <span aria-hidden className="text-sky">*</span>
            <span className="sr-only"> (required)</span>
          </Label>
          <Input
            id={emailId}
            data-testid="login-email-input"
            type="email"
            name="email"
            autoComplete="email"
            required
            aria-required="true"
            aria-invalid={error ? "true" : "false"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={passwordId} className="font-mono-data text-xs uppercase tracking-wider text-ftext2">
            Password <span aria-hidden className="text-sky">*</span>
            <span className="sr-only"> (required)</span>
          </Label>
          <Input
            id={passwordId}
            data-testid="login-password-input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            aria-required="true"
            aria-invalid={error ? "true" : "false"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]"
          />
        </div>
        {error && (
          <p
            id={errorId}
            data-testid="login-error"
            role="alert"
            aria-live="polite"
            className="text-xs text-[#FCA5A5] font-mono-data"
          >
            {error}
          </p>
        )}
        <button
          data-testid="login-submit-btn"
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full bg-sky hover:bg-skyH text-primary-foreground font-mono-data text-sm font-semibold uppercase tracking-wider px-4 py-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex items-center justify-between pt-2 font-mono-data text-xs uppercase tracking-wider">
          <Link data-testid="login-forgot-link" to="/forgot-password" className="text-ftext3 hover:text-sky transition-colors">Forgot password?</Link>
          <Link data-testid="login-portal-link" to="/" className="text-ftext3 hover:text-sky transition-colors">← Portal</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink grid-motif px-4">
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-sky opacity-[0.05] blur-3xl rounded-full pointer-events-none" />
      <main
        role="main"
        aria-labelledby="auth-title"
        className="relative w-full max-w-sm border border-line bg-panel/95 backdrop-blur-md p-6"
      >
        <div className="flex items-center gap-2 mb-6">
          <Logo imgClass="h-9" />
        </div>
        <h1 id="auth-title" className="font-heading font-bold text-2xl text-ftext">{title}</h1>
        {subtitle && <p className="text-sm text-ftext2 mt-2 mb-6 leading-relaxed">{subtitle}</p>}
        {children}
      </main>
    </div>
  );
}
